import { useCustomer, useOpportunity } from 'hooks'
import { useEffect, useState } from 'react'
import type { CareMachineResponse } from './types'

// TODO: temporary n8n webhook — replace with the milestone service / customer-intelligence-service once available
const CARE_MACHINE_URL = 'https://aud-n8n-hook.audibene.net/webhook/care-machine-customer'

/** The fetch inputs a stored response belongs to, so a stale one is never handed back under new ones. */
interface LoadedResponse {
  customerGid?: string
  data: CareMachineResponse | null
  opportunityGid?: string
}

const useCareMachineService = () => {
  const { data: customer } = useCustomer()
  const { opportunity } = useOpportunity()
  const [loaded, setLoaded] = useState<LoadedResponse>({ data: null })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const customerGid = customer?.gid
  const opportunityGid = opportunity?.gid

  useEffect(() => {
    if (!customerGid) return

    const controller = new AbortController()

    const fetchData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const params = new URLSearchParams({ customer_gid: customerGid })

        if (opportunityGid) params.set('opportunity_gid', opportunityGid)

        const response = await fetch(`${CARE_MACHINE_URL}?${params}`, {
          signal: controller.signal,
        })

        if (response.status === 404) {
          setLoaded({ customerGid, data: null, opportunityGid })
          return
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch Care Machine data: ${response.status}`)
        }

        const json = await response.json()

        setLoaded({ customerGid, data: json, opportunityGid })
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError(err as Error)
          // Mark the failure against this customer too, so the error surfaces instead of the
          // hook reporting a perpetual load while the stale-guard below waits for data.
          setLoaded({ customerGid, data: null, opportunityGid })
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()

    return () => controller.abort()
  }, [customerGid, opportunityGid])

  // The fetch is kicked off in an effect, so on the render where the customer changes the previous
  // customer's response is still in state with `isLoading` false. Reporting that as loaded would
  // render one customer's milestones under another — and let a tracked impression fire against the
  // new customer for data nobody has seen.
  // Keyed on every fetch input, not just the customer: an opportunity change under a stable customer
  // reaches the same one-render window and would briefly present the previous opportunity's data.
  const isStale = loaded.customerGid !== customerGid || loaded.opportunityGid !== opportunityGid
  const data = isStale ? null : loaded.data

  return {
    data,
    error,
    // Kept independent of `hasMilestones` — a response can carry issues without milestones (or vice
    // versa), and either surface having content is reason enough to render the card.
    hasIssues: (data?.issues?.length ?? 0) > 0,
    // Exposed so the render guard and any tracked impression share one predicate. An empty
    // `milestones` map is a response with nothing to show, not a response with content.
    hasMilestones: Object.keys(data?.milestones ?? {}).length > 0,
    isLoading: isLoading || (Boolean(customerGid) && isStale),
  }
}

export { useCareMachineService }
export type {
  AppointmentQuestion,
  CareMachineResponse,
  Issue,
  Milestone,
  MilestoneQuote,
  MilestoneStatus,
} from './types'
